/**
 * @description Nested product/variant rows for invoice & estimate Item typeahead (LYN-377).
 * Products with active variants render a non-selectable parent header and indented
 * variant options. Products without variants stay one-click parent selects.
 *
 * @dependencies buildCatalogPickerRows, formatCurrency from invoiceHelpers
 * @notes Do not make the parent row selectable when variants exist — that was the
 * silent first-variant bug. Description/price updates happen in the form handlers.
 */

import {
  buildCatalogPickerRows,
  formatCurrency
} from '../../utils/invoiceHelpers';

/**
 * @param {object} props
 * @param {Array<object>} props.products
 * @param {string} props.search
 * @param {number} [props.limit]
 * @param {(product: object) => void} props.onSelectProduct
 * @param {(product: object, variant: object) => void} props.onSelectVariant
 */
export default function CatalogProductPickerSection({
  products,
  search,
  limit = 8,
  onSelectProduct,
  onSelectVariant
}) {
  const rows = buildCatalogPickerRows(products, search, { limit });
  if (rows.length === 0) return null;

  return (
    <>
      <div className="px-3 py-2 bg-gray-50 border-b border-t border-gray-100">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Products
        </span>
      </div>
      {rows.map((row) => {
        if (row.kind === 'product-header') {
          return (
            <div
              key={row.key}
              className="px-4 pt-2.5 pb-1"
              data-testid={`catalog-picker-header-${row.product.id}`}
            >
              <p className="text-sm font-medium text-gray-700">{row.label}</p>
              {row.sku && (
                <p className="text-xs text-gray-400 mt-0.5">SKU: {row.sku}</p>
              )}
            </div>
          );
        }

        if (row.kind === 'variant') {
          return (
            <button
              key={row.key}
              type="button"
              data-testid={`catalog-picker-variant-${row.variant.id}`}
              onClick={() => onSelectVariant(row.product, row.variant)}
              className="w-full text-left pl-8 pr-4 py-2 hover:bg-gray-50 transition-colors border-l-2 border-transparent hover:border-brand/40 ml-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-gray-900">{row.label}</span>
                <span className="text-sm text-gray-500 shrink-0">
                  {formatCurrency(row.price)}
                </span>
              </div>
            </button>
          );
        }

        return (
          <button
            key={row.key}
            type="button"
            data-testid={`catalog-picker-product-${row.product.id}`}
            onClick={() => onSelectProduct(row.product)}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">{row.label}</span>
              <span className="text-sm text-gray-500">{formatCurrency(row.price)}</span>
            </div>
            {row.sku && (
              <p className="text-xs text-gray-400 mt-0.5">SKU: {row.sku}</p>
            )}
          </button>
        );
      })}
    </>
  );
}
